import { isEqual } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  getDigitalChannelDataSelector,
  getFilterStored,
  getMonitoringError,
  getMonitoringLoading,
  getSensordataSelector,
} from '../../../../../redux/monitoring/selectors';
import {
  fetchMetrics,
  updateFilter,
} from '../../../../../redux/monitoring/thunk';

import {
  CommanderMonitoringFilterLogicalObject,
  getFilterLogicalObjectFromZipDeviceControlUnits,
} from '../util/filter.model';
import { filterAnalogChannels } from '../util/filterAnalogChannels';
import { filterDigitalChannels } from '../util/filterDigitalChannels';
import { useDevicesControlUnits } from '../../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import { STTimeLineChartData, TimeFrame } from '../monitoring.model';
import { filterExtrapolatedDigitalChannelData } from '../util/filterExtrapolatedDigitalChannelData';
// import { useCUsTranslate } from '../../../../../../../util/CUTranslation/hook/useCUsTranslate';
import { checkDefaultChannels } from '../util/checkDefaultChannels';
// import { getLevelOfView } from '../../../../../../../redux/groups/selectors/getLevelOfView';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { toBase64 } from '../../../../../shared/util/base64';
import { useCUsTranslate } from '../../../../../redux/CUTranslation/hook/useCUsTranslate';
import getPaletteColors, {
  getPaletteColorsByControlUnitsDevices,
} from '../util/getPaletteColors';
import { useTemperaturePreference } from '../../../../../service/temperaturePreference';

export interface UseMetricsProps {
  initialTimeFrameStart?: Moment;
  initialTimeFrameEnd?: Moment;
  deviceId?: string;
  commanderId?: string;
}

export const useMetrics = (props: UseMetricsProps) => {
  const {
    initialTimeFrameStart = moment().subtract(6, 'hours'),
    initialTimeFrameEnd = moment(),
    deviceId,
    commanderId,
  } = props;

  const navProps = {
    deviceId: toBase64(deviceId ?? ''),
    commanderId: toBase64(commanderId ?? ''),
  };

  const showLevelView = 'DEVICE';
  const dispatch = useAppDispatch();
  const loadingMetrics = useAppSelector(getMonitoringLoading);
  const errorMetrics = useAppSelector(getMonitoringError);
  const {
    devicesControlUnits,
    loading: loadingDevicesControlUnits,
    refetch: refetchDevicesControlUnits,
  } = useDevicesControlUnits(navProps);
  useEffect(() => {
    refetchDevicesControlUnits();
  }, [refetchDevicesControlUnits]);
  const [, loadingCUsTranslate] = useCUsTranslate(navProps);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const loading = loadingMetrics !== false || !readyToFetch;

  // Filter controls
  const [timeFrame, setTimeFrame] = useState<TimeFrame>({
    start: initialTimeFrameStart,
    end: initialTimeFrameEnd,
  });

  const defaultFilterObjectEventsStored = useAppSelector(
    (store) => getFilterStored(store, { ...navProps, type: 'events' }),
    isEqual
  );
  const defaultFilterObjectChannelsStored = useAppSelector(
    (store) => getFilterStored(store, { ...navProps, type: 'channels' }),
    isEqual
  );
  const defaultFilterObjectChannels = useMemo(
    () =>
      // Hydrate stored value
      defaultFilterObjectChannelsStored
        ? defaultFilterObjectChannelsStored
        : // Get default
          checkDefaultChannels(
            getFilterLogicalObjectFromZipDeviceControlUnits(
              devicesControlUnits,
              'channels',
              false
            )
          ),
    // I don't want update this every time value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devicesControlUnits]
  );
  const defaultFilterObjectEvents = useMemo(
    () =>
      // Hydrate stored value
      defaultFilterObjectEventsStored
        ? defaultFilterObjectEventsStored
        : // Get default
          checkDefaultChannels(
            getFilterLogicalObjectFromZipDeviceControlUnits(
              devicesControlUnits,
              'events',
              false
            )
          ),
    // I don't want update this every time value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devicesControlUnits]
  );
  const digitalControls = useState<CommanderMonitoringFilterLogicalObject>(
    defaultFilterObjectEvents
  );
  const analogControls = useState<CommanderMonitoringFilterLogicalObject>(
    defaultFilterObjectChannels
  );

  const [digitalFilterObjectEvents, setDigitalFilterObject] = digitalControls;
  const [analogFilterObjectEvents, setAnalogFilterObject] = analogControls;
  const externalSetDigitalFilterObject: typeof setDigitalFilterObject = (
    value
  ) => {
    setDigitalFilterObject(value);
    dispatch(
      updateFilter({
        ...navProps,
        type: 'events',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };
  const externalSetAnalogFilterObject: typeof setAnalogFilterObject = (
    value
  ) => {
    setAnalogFilterObject(value);
    dispatch(
      updateFilter({
        ...navProps,
        type: 'channels',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };

  // getting data

  const sensordata = useAppSelector((state) =>
    getSensordataSelector(state, navProps)
  );
  const digitalChannels = useAppSelector((state) =>
    getDigitalChannelDataSelector(state, navProps)
  );
  const sensordataFiltered = useMemo(() => {
    if (loading) return [];
    return filterAnalogChannels(sensordata, analogFilterObjectEvents);
  }, [sensordata, analogFilterObjectEvents, loading]);

  const digitalChannelsFiltered = useMemo(() => {
    if (loading) return {} as STTimeLineChartData;
    return filterExtrapolatedDigitalChannelData(
      filterDigitalChannels(digitalChannels, digitalFilterObjectEvents),
      timeFrame.start.unix(),
      timeFrame.end.unix()
    );
  }, [
    digitalChannels,
    digitalFilterObjectEvents,
    loading,
    timeFrame.end,
    timeFrame.start,
  ]);

  // generate colors
  const paletteColors = useMemo(() => {
    const paletteColorsControlUnits = getPaletteColorsByControlUnitsDevices(
      analogFilterObjectEvents,
      digitalFilterObjectEvents
    );
    return getPaletteColors(
      paletteColorsControlUnits,
      digitalChannelsFiltered,
      sensordataFiltered
    );
  }, [
    analogFilterObjectEvents,
    digitalFilterObjectEvents,
    digitalChannelsFiltered,
    sensordataFiltered,
  ]);

  // Fetch
  useEffect(() => {
    if (
      !(loadingDevicesControlUnits || loadingCUsTranslate) &&
      readyToFetch !== true
    ) {
      setDigitalFilterObject(defaultFilterObjectEvents);
      setAnalogFilterObject(defaultFilterObjectChannels);
      setReadyToFetch(true);
    }
  }, [
    defaultFilterObjectChannels,
    defaultFilterObjectEvents,
    loadingCUsTranslate,
    loadingDevicesControlUnits,
    readyToFetch,
    setAnalogFilterObject,
    setDigitalFilterObject,
  ]);
  const temperaturePreference = useTemperaturePreference();
  useEffect(() => {
    if (readyToFetch) {
      dispatch(
        fetchMetrics({
          devicesControlUnits,
          start: timeFrame.start.valueOf(),
          end: timeFrame.end.valueOf(),
          analogFilterObjectEvents,
          digitalFilterObjectEvents,
          showLevelOption: showLevelView,
          temperaturePreference,
        })
      );
    }
    // ignore devicesControlUnits as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeFrame,
    dispatch,
    readyToFetch,
    analogFilterObjectEvents,
    digitalFilterObjectEvents,
  ]);

  return {
    timeControls: { timeFrame, setTimeFrame },
    digitalControls: [
      digitalFilterObjectEvents,
      externalSetDigitalFilterObject,
    ] as typeof digitalControls,
    analogControls: [
      analogFilterObjectEvents,
      externalSetAnalogFilterObject,
    ] as typeof analogControls,
    metrics: {
      sensordata: sensordataFiltered,
      digitalChannels: digitalChannelsFiltered,
    },
    paletteColors,
    loading,
    error: errorMetrics,
  } as const;
};
